import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users, FileText, DollarSign, Calendar } from 'lucide-react';
import { VendedorDetailedMetrics } from '@/types/vendedores';

interface VendedorPerformanceTabProps {
  vendedor: VendedorDetailedMetrics;
}

export function VendedorPerformanceTab({ vendedor }: VendedorPerformanceTabProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatMonth = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Ordenar métricas por data (mais recente primeiro)
  const metricsOrdenadas = [...vendedor.metrics_por_periodo].sort(
    (a, b) => new Date(b.mes).getTime() - new Date(a.mes).getTime()
  );

  // Calcular totais
  const totais = metricsOrdenadas.reduce(
    (acc, m) => ({
      leads: acc.leads + m.leads,
      propostas: acc.propostas + m.propostas,
      contratos: acc.contratos + m.contratos,
      valor_propostas: acc.valor_propostas + m.valor_propostas,
      valor_contratos: acc.valor_contratos + m.valor_contratos,
      parceiros_novos: acc.parceiros_novos + m.parceiros_novos
    }),
    { leads: 0, propostas: 0, contratos: 0, valor_propostas: 0, valor_contratos: 0, parceiros_novos: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Resumo de Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total no Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Leads:</span>
                <span className="font-bold">{totais.leads}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Propostas:</span>
                <span className="font-bold">{totais.propostas}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Contratos:</span>
                <span className="font-bold">{totais.contratos}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Valores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Propostas</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(totais.valor_propostas)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Contratos</p>
                <p className="text-lg font-bold text-purple-600">
                  {formatCurrency(totais.valor_contratos)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Crescimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Novos Parceiros</p>
                <p className="text-2xl font-bold text-blue-600">
                  +{totais.parceiros_novos}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Taxa de Conversão Média</p>
                <p className="text-lg font-bold">
                  {vendedor.taxa_conversao_leads_contratos}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Histórico Mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Histórico Mensal (Últimos 12 Meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {metricsOrdenadas.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum dado de performance disponível para este período
            </p>
          ) : (
            <div className="space-y-3">
              {metricsOrdenadas.map((metric, index) => (
                <div
                  key={metric.mes}
                  className={`p-4 rounded-lg border ${
                    index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{formatMonth(metric.mes)}</span>
                      {index === 0 && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                          Mais Recente
                        </span>
                      )}
                    </div>
                    {metric.parceiros_novos > 0 && (
                      <div className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        <Users className="h-3 w-3" />
                        +{metric.parceiros_novos} parceiros
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-lg font-bold text-blue-600">{metric.leads}</p>
                      <p className="text-xs text-muted-foreground">Leads</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-lg font-bold text-green-600">{metric.propostas}</p>
                      <p className="text-xs text-muted-foreground">Propostas</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-lg font-bold text-purple-600">{metric.contratos}</p>
                      <p className="text-xs text-muted-foreground">Contratos</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-sm font-bold text-green-600">
                        {formatCurrency(metric.valor_propostas)}
                      </p>
                      <p className="text-xs text-muted-foreground">Valor Prop.</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-sm font-bold text-purple-600">
                        {formatCurrency(metric.valor_contratos)}
                      </p>
                      <p className="text-xs text-muted-foreground">Valor Contr.</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ranking de Parceiros (Top 5) */}
      {vendedor.parceiros.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top 5 Parceiros por Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {vendedor.parceiros
                .sort((a, b) => b.valor_contratos - a.valor_contratos)
                .slice(0, 5)
                .map((parceiro, index) => (
                  <div
                    key={parceiro.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm
                        ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                          index === 1 ? 'bg-gray-100 text-gray-700' : 
                          index === 2 ? 'bg-orange-100 text-orange-700' : 
                          'bg-blue-100 text-blue-700'}
                      `}>
                        {index + 1}º
                      </div>
                      <div>
                        <p className="font-semibold">{parceiro.nome}</p>
                        <p className="text-xs text-muted-foreground">
                          {parceiro.cidade}/{parceiro.estado}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-600">
                        {formatCurrency(parceiro.valor_contratos)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {parceiro.contratos_fechados} contratos
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

